import { Component, EventEmitter, Input, Output } from '@angular/core';
import { Address } from '../../../core/models/address';
import { FormsService, TranslatedFormGroup } from '../../../core/services/forms.service';
import { ValidationResult, ValidatorFactories } from '../../../core/validators';
import { ApiOrder, ApiOrderline } from '../../../cart/models/cart-models';
import { Observable, Subject } from 'rxjs';
import * as moment from 'moment';
import { select, Store } from '@ngrx/store';
import { Location } from '@angular/common';
import { Validators } from '@angular/forms';
import * as fromBase from '../../../base/reducers';
import { debounceTime, distinctUntilChanged, filter, map } from 'rxjs/operators';
import chaining from '../../../core/helpers/chaining';
import { Setting } from '../../../core/models/setting';

const referenceValidator = ValidatorFactories.reference(8)
const dateValidator = ValidatorFactories.date('mm-DD-yyyy')

@Component({
  selector: 'app-checkout-second-step-page',
  templateUrl: './template.html',
  styleUrls: ['./styles.scss']
})

export class CheckoutSecondStepPageComponent {
  billingAddress: Address
  deliveryAddress: Address

  orderForm: TranslatedFormGroup
  validationResult: ValidationResult

  order: ApiOrder

  setDetails$ = new Subject<any>()
  fullWidthView$: Observable<boolean>

  @Input() addresses: Address[]
  @Input() checkoutPageSettings: Setting
  @Input('order') set orderSetter(value: ApiOrder) {
    if(!value) return

    this.order = value

    this.orderForm.patchValue({
      dlvDate: value.dlvDate && moment(value.dlvDate).format('DD-MM-YYYY'),
      customerReference: value.customerReference,
      comments: value.comments
    })

    let address = this.addresses && Array.isArray(this.addresses)
      ? this.addresses.find(a => a.code === value.dlvAddress)
      : null

    this.setAddress('delivery', address)
  }
  @Input() committing: boolean
  @Input() errors: any

  @Output() detailsChanged = new EventEmitter<ApiOrder>()
  @Output() createOrder = new EventEmitter<ApiOrder>()

  constructor(
    private forms: FormsService,
    private store: Store<any>,
    private _location: Location
  ) {
    this.orderForm = this.forms.group({
      dlvDate: [
        this.deliveryDate, [Validators.required, dateValidator], 'checkout.fields.deliverydate'
      ],
      customerReference: [
        this.reference, [referenceValidator], 'checkout.fields.reference'
      ],
      comments: [
        this.remarks, [], 'checkout.fields.comments'
      ]
    })

    this.fullWidthView$ = store
      .pipe(
        select(fromBase.isFullWidthViewEnabled)
      )
  }

  ngOnInit() {
    let withDelivery = (details => ({
      ...details,
      dlvAddress: this.deliveryAddress && this.deliveryAddress.code
    })).bind(this)
    let withOrder = (details => ({
      ...this.order,
      ...details
    }))

    this.orderForm.valueChanges.pipe(
      filter((val => !(
        val.dlvDate === moment(this.order.dlvDate).format('DD-MM-YYYY')
        && val.customerReference === this.order.customerReference
        && val.comments === this.order.comments
      )).bind(this)),
      map(withDelivery),)
      .subscribe(this.setDetails$)

    this.setDetails$.pipe(
      distinctUntilChanged(
        null, val => JSON.stringify(val)
      ),
      map(withOrder),
      debounceTime(500))
      .subscribe(this.detailsChanged)
  }

  get deliveryDate(): string {
    return (this.order && this.order.dlvDate) || ''
  }

  get reference(): string {
    return (this.order && this.order.customerReference) || ''
  }

  get remarks(): string {
    return (this.order && this.order.comments) || ''
  }

  get currency(): string {
    return chaining(this.order, 'currency')
  }

  get lines(): ApiOrderline[] {
    return (this.order && this.order.lines) || []
  }

  get hasLines(): boolean {
    return !!this.lines.length
  }

  get deliveryAddressNumber(): string {
    return chaining(this.order, 'dlvAddress')
  }

  setAddress(type: 'billing' | 'delivery', address: Address){
    if(!address)
      return

    switch(type){
      case 'billing':
        this.billingAddress = address
        break;
      case 'delivery':
        this.deliveryAddress = address
        break;
    }

    if(type === 'delivery' && this.order && this.order.dlvAddress !== address.code)
      this.setDetails$.next({
        ...this.orderForm.value,
        dlvAddress: address.code
      })
  }

  submit() {
    if(!this.orderForm.valid) {
      this.validationResult = this.orderForm.validate()
    } else {
      this.validationResult = null
      this.createOrder.emit({
        ...this.order,
        ...this.orderForm.value,
        dlvAddress: this.deliveryAddress && this.deliveryAddress.code
      })
    }
  }
}
